import { useRef, useEffect } from 'react';
import CloseIcon from '@/assets/Icons/components/CloseIcon';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { PostHogMessages } from '@/utils/PostHogMessages';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  data: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  showCloseButton?: boolean;
  modalName?: string;
}

const InformationModal = ({
  isOpen,
  onClose,
  title,
  data,
  size = 'md',
  showCloseButton = true,
  modalName = 'generic_model',
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Handle escape key press
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Add event listeners when modal is open
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      trackEvent(PostHogEvents.MODAL_OPENED, { modal_name: modalName });
    } else {
      trackEvent(PostHogEvents.MODAL_OPENED, { modal_name: modalName });
    }

    return () => {
      // Clean up event listeners
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      // Restore body scrolling
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    xxl: 'max-w-3xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        ref={modalRef}
        className={`bg-white rounded-lg shadow-lg w-full ${sizeClasses[size]} mx-4 overflow-hidden`}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-3">
          <h3 className="text-lg font-medium text-ds-gray-700">{title}</h3>
          {showCloseButton && (
            <button
              type="button"
              onClick={() => {
                onClose();
                trackEvent(PostHogEvents.MODAL_CLOSED, {
                  model_name: modalName,
                  message: PostHogMessages[PostHogEvents.MODAL_CLOSED],
                });
              }}
              className="text-ds-gray-400 hover:text-ds-gray-600 focus:outline-none"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div
          className="px-6 py-4 max-h-[80vh]  overflow-y-auto whitespace-pre-wrap text-sm"
          dangerouslySetInnerHTML={{ __html: data }}
        />
      </div>
    </div>
  );
};

export default InformationModal;
